import { FC, useContext } from 'react';
import CustomerDetailSection from '../../components/CustomerDetailSection';
import JobDetailsOverviewSection from '../../components/JobDetailsOverviewSection';
import { Job } from 'containers/jobs/jobs.model';
import { AddressField, InvoiceStatus } from 'containers/invoices/invoices.model';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import ProfitabilitySummary, { ProfitabilitySummaryData } from 'containers/jobs/components/ProfitabilitySummary';
import { If } from 'components';
import { useAppSelector } from 'hooks';
import { selectJobLineItems } from 'containers/jobs/create-edit-job/createJobSlice';
import { JobPermissionsContext } from '../JobDetails';
import { checkIfInvoiceIsPaid } from 'containers/invoices/utils';

interface OverviewTabProps {
  job: Job;
  onCustomerClick: () => void;
  onEditScheduleClick: () => void;
  onEditAssigneesClick: () => void;
  onEditLeadSourceClick: () => void;
  onEditExternalCompanyClick: () => void;
  onEditDescriptionClick: () => void;
  onEditAddress?: (address: AddressField, type: ADDRESS_TYPE) => void;
  isSingleMemberTeam: boolean;
  onViewBillingDetails: () => void;
  profitabilityData?: ProfitabilitySummaryData;
  isLoading: boolean;
  serviceAreas?: any[];
  onRefreshServiceAreas?: () => void;
  refetchJob: () => void;
}

const OverviewTab: FC<OverviewTabProps> = ({
  job,
  onCustomerClick,
  onEditScheduleClick,
  onEditAssigneesClick,
  onEditLeadSourceClick,
  onEditExternalCompanyClick,
  onEditDescriptionClick,
  onEditAddress,
  isSingleMemberTeam,
  onViewBillingDetails,
  profitabilityData,
  isLoading,
  serviceAreas,
  onRefreshServiceAreas,
  refetchJob
}) => {
  const jobLineItemsState = useAppSelector(selectJobLineItems);
  const { canEditJobs, canViewJobCostingsAndMargin } = useContext(JobPermissionsContext);
  const isPaidInvoice =
    job.invoices?.length > 0 && job.invoices.some(invoice => checkIfInvoiceIsPaid(invoice.status as InvoiceStatus));

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6 divide-y">
        <CustomerDetailSection
          onCustomerClick={onCustomerClick}
          onEditAddress={onEditAddress}
          canEditJobs={canEditJobs}
          job={job}
          serviceAreas={serviceAreas}
          onRefreshServiceAreas={onRefreshServiceAreas}
          refetchJob={refetchJob}
        />
        <JobDetailsOverviewSection
          job={job}
          onEditScheduleClick={onEditScheduleClick}
          onEditAssigneesClick={onEditAssigneesClick}
          onEditLeadSourceClick={onEditLeadSourceClick}
          onEditExternalCompanyClick={onEditExternalCompanyClick}
          onEditDescriptionClick={onEditDescriptionClick}
          isSingleMemberTeam={isSingleMemberTeam}
        />
        <If condition={jobLineItemsState?.length > 0 && canViewJobCostingsAndMargin}>
          <div className="pt-5">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-17px font-bold text-primaryText">Profitability summary</div>
              <button onClick={onViewBillingDetails} className="cursor-pointer text-sbase font-semibold text-secondary">
                View billing details
              </button>
            </div>
            {<ProfitabilitySummary data={profitabilityData} isPaidInvoice={isPaidInvoice} isLoading={isLoading} />}
          </div>
        </If>
      </div>
    </div>
  );
};

export default OverviewTab;
